import { useCallback } from 'react';

export const DEFAULT_IDS_TO_PERSIST: string[] = ['ariaLiveWrapper'];

interface FocusTrapCallbacks {
    createFocusTrap: () => void;
    relinquishFocusTrap: () => void;
}

const saveCheckPointAndHideElement = (
    visibilityMap: Map<string, string>,
    ariaVisibleMap: Map<string, boolean>,
    element: HTMLElement,
    hideVisibility: boolean = false
) => {
    const elementId: string = element?.id;

    /**
     * Aria-hidden Properties
     */
    // If the element is not originally aria hidden and it is not in the set, add it to the set
    ariaVisibleMap?.set(elementId, element.getAttribute('aria-hidden') === 'true');

    // Now set its aria-hidden value to true
    element.setAttribute('aria-hidden', 'true');

    /**
     * Visibility properties
     */
    const currentVisibility: string = element?.style?.visibility;
    currentVisibility !== 'hidden' &&
        !visibilityMap?.has(elementId) &&
        visibilityMap.set(elementId, currentVisibility);

    // Hide the visibility for the element
    if (element.style && hideVisibility) {
        element.style.visibility = 'hidden';
    }
};

/**
 * Returns creates and relinquish focus trap methods for an element
 * @param id
 * @param elements
 * @param hideBackground
 * @param exclusions
 */
export const useFocusTrap = (
    id: string,
    hideBackground: boolean = false,
    exclusions: string[] = DEFAULT_IDS_TO_PERSIST
): FocusTrapCallbacks => {
    const elements: HTMLCollection = document.body.children;
    const deps = [id, hideBackground, exclusions];

    const visibilityMap = new Map<string, string>();
    const ariaVisibleMap = new Map<string, boolean>();

    const createFocusTrap = useCallback(() => {
        /** We iterate through all the elements passed and barring the exclusions, we store the elements current state in our map */
        for (let elementIndex: number = 0; elementIndex < elements.length; elementIndex++) {
            const element = elements[elementIndex];
            const elementId: string = element?.id;
            if (exclusions?.indexOf(elementId) === -1 && elementId && id) {
                // store the previous properties in the state

                const htmlElement = element as HTMLElement;

                // Unless it is the current focus id, make its aria-hidden to true
                if (id === elementId) {
                    element.removeAttribute('aria-hidden');
                } else {
                    saveCheckPointAndHideElement(
                        visibilityMap,
                        ariaVisibleMap,
                        htmlElement,
                        hideBackground
                    );
                }
            }
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps --
         * See aka.ms/mini-lint-errors
         *	> React Hook useCallback was passed a dependency list that is not an array literal. This means we can't statically verify whether you've passed the correct dependencies.
         *	> React Hook useCallback has missing dependencies: 'ariaVisibleMap', 'elements', 'exclusions', 'hideBackground', 'id', and 'visibilityMap'. Either include them or remove the dependency array. */
    }, deps);

    const relinquishFocusTrap = useCallback(() => {
        // For all elements whose state is known, restore the state
        for (let elementIndex: number = 0; elementIndex < elements.length; elementIndex++) {
            const element = elements[elementIndex] as HTMLElement;
            const elementId = element?.id;

            // Excluded or invalid case
            if (!elementId || exclusions.indexOf(elementId) !== -1) {
                continue;
            }

            // Restore aria-hidden
            if (ariaVisibleMap?.has(elementId)) {
                const ariaHidden: boolean = ariaVisibleMap?.get(elementId);
                if (elementId && !ariaHidden) {
                    // Only if the previous state is not aria hidden, remove the aria-hidden attribute from the current element
                    element.removeAttribute('aria-hidden');
                } else {
                    element.setAttribute('aria-hidden', 'true');
                }
            }

            // restore visibility property
            if (visibilityMap.has(elementId)) {
                element.style.visibility = visibilityMap.get(elementId);
            }
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps --
         * See aka.ms/mini-lint-errors
         *	> React Hook useCallback was passed a dependency list that is not an array literal. This means we can't statically verify whether you've passed the correct dependencies.
         *	> React Hook useCallback has missing dependencies: 'ariaVisibleMap', 'elements', 'exclusions', and 'visibilityMap'. Either include them or remove the dependency array. */
    }, deps);

    return { createFocusTrap, relinquishFocusTrap };
};
